import { ISanityImage } from '@rbi-ctg/menu';
import { IMenuObject } from 'state/menu/types';

export interface IMenuObjectDisplayData {
  name?: string;
  image?: ISanityImage;
}

/**
 * Cart/order entry name/image are baked in at add-to-cart or order-placement time and never
 * re-resolved after a later app language change. A fresh, currently-localized legacy
 * `menuObject` (fetched once for the whole item/combo) already contains the full nested tree
 * (combo slots -> items -> item options -> modifiers) with correctly-localized display data —
 * walk it once to build an id/key -> { name, image } map so every entry in that tree, not just
 * the top-level one, can be re-localized by looking up its own `_id`/`_key`/`sanityId`.
 */
export const getMenuObjectNameMap = (
  menuObject: IMenuObject | null | undefined
): Map<string, IMenuObjectDisplayData> => {
  const displayDataMap = new Map<string, IMenuObjectDisplayData>();
  if (!menuObject) {
    return displayDataMap;
  }

  const visit = (node: unknown) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    const id = '_id' in node ? (node as { _id?: string })._id : undefined;
    const key = '_key' in node ? (node as { _key?: string })._key : undefined;
    // An ItemOptionModifier commonly has no direct `name` at all — `makeItemOptionModifier`
    // (utils/menu/modifiers.ts) composes its displayed name from
    // `modifierMultiplier.prefix.locale` + `modifierMultiplier.modifier.name.locale` in that
    // case, so this map must fall back to the same composition to stay consistent, otherwise
    // that modifier's description keeps showing its stale baked-in language.
    const modifierMultiplier = (node as {
      modifierMultiplier?: {
        prefix?: { locale?: string | null } | null;
        modifier?: { name?: { locale?: string | null } | null } | null;
      } | null;
    }).modifierMultiplier;
    const composedModifierName = modifierMultiplier
      ? `${modifierMultiplier.prefix?.locale ?? ''} ${
          modifierMultiplier.modifier?.name?.locale ?? ''
        }`.trim()
      : undefined;
    const name =
      (node as { name?: { locale?: string | null } }).name?.locale ||
      composedModifierName ||
      undefined;

    // `image.asset` is nullable at the network level even when `image` itself is a non-null
    // wrapper (IMenuImageFragment) — an asset-less image must not be treated as usable.
    const image = (node as { image?: ISanityImage }).image;
    const hasUsableImage = !!image?.asset?._id;

    if (name || hasUsableImage) {
      const displayData: IMenuObjectDisplayData = {
        ...(name && { name }),
        ...(hasUsableImage && { image }),
      };
      if (id) {
        displayDataMap.set(id, displayData);
      }
      if (key) {
        displayDataMap.set(key, displayData);
      }
    }

    if ('mainItem' in node) {
      visit((node as { mainItem?: unknown }).mainItem);
    }
    if ('option' in node) {
      visit((node as { option?: unknown }).option);
    }
    ('options' in node ? (node as { options?: unknown[] }).options : [])?.forEach(visit);
  };

  visit(menuObject);

  return displayDataMap;
};
